import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt, faSave, faCheckCircle, faExclamationTriangle,
  faSpinner, faUser, faIdCard, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
  }
};

const API_URL = `${API_CONFIG2.apiUrl}presence/api_presenceNew.php`;

const ManagePresence = ({ userId }) => {
  const [employes, setEmployes] = useState([]);
  const [presences, setPresences] = useState({}); // { employeId: 'P'|'R'|'A' }
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Récupération des employés et des présences
  const fetchData = useCallback(async (selectedDate) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      // Récupérer les employés
      const empUrl = `${API_URL}?action=employes&unique=${new Date().getTime()}`;
      const empData = await fetchJSON(empUrl, { signal: controller.signal });
      if (empData.status !== 'success') {
        throw new Error(empData.message || 'Erreur chargement employés');
      }
      if (!isMountedRef.current) return;
      setEmployes(empData.data);

      // Récupérer les présences pour la date
      const presUrl = `${API_URL}?action=fetch_presence&date=${selectedDate}&unique=${new Date().getTime()}`;
      const presData = await fetchJSON(presUrl, { signal: controller.signal });
      if (presData.status !== 'success') {
        throw new Error(presData.message || 'Erreur chargement présences');
      }
      if (!isMountedRef.current) return;
      setPresences(presData.data || {});
    } catch (err) {
      if (err.name === 'AbortError') return;
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    isMountedRef.current = true;
    fetchData(date);
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []); // eslint-disable-line

  // Changement de date
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDate(newDate);
    fetchData(newDate);
  };

  // Mise à jour du statut d'un employé
  const handleStatutChange = (employeId, statut) => {
    setPresences(prev => ({ ...prev, [employeId]: statut }));
  };

  // Enregistrement des présences
  const handleSave = async () => {
    setSaving(true);
    try {
      // Construire le payload : on envoie tous les employés avec leur statut (ou 'A' par défaut)
      const presenceData = {};
      employes.forEach(emp => {
        presenceData[emp.id] = presences[emp.id] || 'A'; // absent par défaut
      });

      const payload = {
        date,
        presences: presenceData,
        userId
      };

      const data = await fetchJSON(`${API_URL}?action=save_presence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        // Recharger les données pour refléter les changements
        fetchData(date);
      } else {
        throw new Error(data.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Rafraîchir
  const refresh = () => {
    fetchData(date);
  };

  // Obtenir le chemin de la photo (à adapter selon votre structure)
  const getPhotoUrl = (photo) => {
    if (!photo || photo === 'default.png') {
      return '/assets/default-avatar.png'; // remplacez par votre default
    }
    // Supposons que les photos sont stockées dans un dossier 'uploads/employes/'
    return `${API_CONFIG2.apiUrl}uploads/employes/${photo}`;
  };

  // Déterminer la classe CSS pour le statut
  const getStatutClass = (statut) => {
    switch (statut) {
      case 'P': return 'statut-present';
      case 'R': return 'statut-retard';
      case 'A': return 'statut-absent';
      default: return '';
    }
  };

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #4f8cff;
          --accent-green: #22c55e;
          --accent-orange: #f59e0b;
          --accent-red: #ef4444;
          --bg-start: #0d1b2a;
          --bg-end: #0a0f1f;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(79, 140, 255, 0.15);
          --text-primary: #e8edff;
          --text-secondary: #a5b4d9;
          --text-muted: #5e6f94;
          --radius: 20px;
          --shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
        }
        * { box-sizing: border-box; }
        .monetra-page {
          background: radial-gradient(ellipse at 20% 30%, #132a4a, #0a0f1f);
          min-height: 100vh;
          padding: clamp(0.8rem, 2vw, 2.5rem);
          color: var(--text-primary);
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 2vw, 2rem);
          box-shadow: var(--shadow);
          transition: all 0.25s ease;
        }
        .glass-card:hover {
          border-color: rgba(79, 140, 255, 0.25);
          box-shadow: 0 16px 48px rgba(79, 140, 255, 0.05);
        }
        .monetra-btn {
          padding: 8px 18px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.95rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.3);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px rgba(79, 140, 255, 0.25);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: #2563eb;
          box-shadow: 0 8px 28px rgba(79, 140, 255, 0.4);
        }
        .monetra-btn.success {
          background: var(--accent-green);
          color: #fff;
          border: none;
        }
        .monetra-btn.success:hover:not(:disabled) {
          background: #16a34a;
        }
        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 10px 14px;
          transition: 0.3s;
          font-size: clamp(0.9rem, 1.1vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.06);
        }
        .monetra-input[type="date"] {
          color-scheme: dark;
        }

        .presence-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.2rem;
          margin-top: 1rem;
        }

        .presence-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: 0.2s;
        }
        .presence-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(79, 140, 255, 0.2);
        }
        .presence-avatar {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid rgba(255,255,255,0.1);
          flex-shrink: 0;
          background: #1e293b;
        }
        .presence-info {
          flex: 1;
          min-width: 0;
        }
        .presence-info .name {
          font-weight: 600;
          font-size: 1rem;
          margin-bottom: 2px;
        }
        .presence-info .matricule {
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .statut-group {
          display: flex;
          gap: 6px;
          flex-shrink: 0;
        }
        .statut-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.1);
          background: transparent;
          color: var(--text-secondary);
          font-weight: 700;
          font-size: 0.85rem;
          cursor: pointer;
          transition: 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .statut-btn:hover {
          border-color: rgba(255,255,255,0.3);
          transform: scale(1.05);
        }
        .statut-btn.active-P {
          background: rgba(34, 197, 94, 0.2);
          border-color: var(--accent-green);
          color: var(--accent-green);
        }
        .statut-btn.active-R {
          background: rgba(245, 158, 11, 0.2);
          border-color: var(--accent-orange);
          color: var(--accent-orange);
        }
        .statut-btn.active-A {
          background: rgba(239, 68, 68, 0.2);
          border-color: var(--accent-red);
          color: var(--accent-red);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.85) !important;
          backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(79, 140, 255, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-green); }
        .text-danger { color: var(--accent-red); }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-center { text-align: center; }
        .ms-auto { margin-left: auto; }

        @media (max-width: 768px) {
          .monetra-page { padding: 10px; }
          .glass-card { padding: 1rem; border-radius: 16px; }
          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .presence-grid {
            grid-template-columns: 1fr;
          }
          .presence-card {
            flex-wrap: wrap;
            justify-content: center;
            text-align: center;
          }
          .statut-group {
            margin-top: 6px;
          }
        }
      `}</style>

      <div className="monetra-page">
        {/* En-tête */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              Contrôle de présence
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              {employes.length} employé(s) actif(s)
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="monetra-btn" onClick={refresh} disabled={loading || saving}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || saving} /> Actualiser
            </button>
            <button className="monetra-btn success" onClick={handleSave} disabled={loading || saving}>
              {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Enregistrement…</> : <><FontAwesomeIcon icon={faSave} /> Enregistrer</>}
            </button>
          </div>
        </div>

        {/* Sélecteur de date */}
        <div className="glass-card mb-4 d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-secondary" />
            <span className="text-secondary small">Date :</span>
            <input
              type="date"
              className="monetra-input"
              value={date}
              onChange={handleDateChange}
              style={{ maxWidth: '200px' }}
            />
          </div>
          <div className="ms-auto d-flex gap-3 text-secondary small">
            <span><span className="statut-btn active-P" style={{ width: '20px', height: '20px', display: 'inline-block', marginRight: '4px' }}>P</span> Présent</span>
            <span><span className="statut-btn active-R" style={{ width: '20px', height: '20px', display: 'inline-block', marginRight: '4px' }}>R</span> Retard</span>
            <span><span className="statut-btn active-A" style={{ width: '20px', height: '20px', display: 'inline-block', marginRight: '4px' }}>A</span> Absent</span>
          </div>
        </div>

        {/* Grille des employés */}
        <div className="glass-card">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-secondary mt-3">Chargement des données...</p>
            </div>
          ) : (
            <div className="presence-grid">
              {employes.map(emp => {
                const statut = presences[emp.id] || 'A';
                return (
                  <div key={emp.id} className="presence-card">
                    <img
                      src={`${API_CONFIG2.apiUrl}${emp.photo}`}
                      alt={`${emp.prenom} ${emp.nom}`}
                      className="presence-avatar"
                      onError={(e) => { e.target.src = '/assets/default-avatar.png'; }}
                    />
                    <div className="presence-info">
                      <div className="name">{emp.prenom} {emp.nom}</div>
                      <div className="matricule"><FontAwesomeIcon icon={faIdCard} className="me-1" />{emp.matricule}</div>
                    </div>
                    <div className="statut-group">
                      {['P', 'R', 'A'].map(s => (
                        <button
                          key={s}
                          className={`statut-btn ${statut === s ? `active-${s}` : ''}`}
                          onClick={() => handleStatutChange(emp.id, s)}
                          disabled={loading || saving}
                          title={s === 'P' ? 'Présent' : s === 'R' ? 'Retard' : 'Absent'}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Toast de notification */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-monetra p-3 ${toast.variant}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span>{toast.message}</span>
              <button
                className="ms-auto"
                onClick={() => setToast(prev => ({ ...prev, show: false }))}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManagePresence;